import { useEffect, useSyncExternalStore } from 'react';

import { api } from '../api/shop';
import type { Deal } from '../api/types';

/**
 * A real photo for a deal that has none, or whose photo will not load.
 *
 * Some deals were published without a photo, and a few name files that were never
 * uploaded. A grey "deal photo" box on those cards made the shop look unfinished,
 * so a deal without a working photo borrows one from another deal: one in the same
 * category when there is one, otherwise any. The borrowed photo is DISPLAY ONLY -
 * nothing is written back to the deal.
 *
 * THE SAME DEAL ALWAYS GETS THE SAME PHOTO. The choice is a hash of the deal id over
 * one shared pool (the full catalogue, fetched once per visit), so a card in the
 * grid and the gallery on its detail page show the same picture, and a reload does
 * not reshuffle the shop.
 *
 * A photo that fails to load anywhere is dropped from the pool, so a missing file is
 * never handed to another deal.
 */

interface PoolPhoto {
  url: string;
  category: string;
}

let pool: PoolPhoto[] = [];
const broken = new Set<string>();
let version = 0;
let requested = false;
const listeners = new Set<() => void>();

function changed() {
  version++;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** How long a photo gets to prove it loads before it is left out of the pool. */
const PROBE_TIMEOUT_MS = 8000;

/** Resolves true when the photo actually loads - a URL in the catalogue is not proof of a file. */
function loads(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    const image = new Image();
    const timer = window.setTimeout(() => resolve(false), PROBE_TIMEOUT_MS);
    image.onload = () => {
      window.clearTimeout(timer);
      resolve(image.naturalWidth > 0);
    };
    image.onerror = () => {
      window.clearTimeout(timer);
      resolve(false);
    };
    image.src = url;
  });
}

/**
 * Fetch the catalogue once per visit, keep only the photos that really load, and
 * publish the pool ONCE, when every check has finished. Publishing as each one
 * arrived would let a card pick from a half-checked pool - and a detail page, loaded
 * fresh, would pick differently from the card that linked to it.
 *
 * A failure leaves the placeholder and allows a later retry.
 */
function ensurePool() {
  if (requested) return;
  requested = true;

  api
    .deals()
    .then(async (deals: Deal[]) => {
      const candidates = deals.flatMap((d) => (d.imageUrl === null ? [] : [{ url: d.imageUrl, category: d.category }]));
      const urls = [...new Set(candidates.map((c) => c.url))];
      const ok = await Promise.all(urls.map(loads));
      const working = new Set(urls.filter((_, i) => ok[i]));

      urls.filter((url) => !working.has(url)).forEach((url) => broken.add(url));
      pool = candidates.filter((c) => working.has(c.url));
      changed();
    })
    .catch(() => {
      requested = false;
    });
}

/** Call from an image's onError: that URL is never offered as a stand-in again. */
export function markPhotoBroken(url: string) {
  if (broken.has(url)) return;
  broken.add(url);
  changed();
}

/** FNV-1a: a small, stable string hash. Stability is all that matters here. */
function hash(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * The stand-in for one deal, or null when there is nothing to borrow.
 * Pure, so the choice can be reasoned about (and checked) apart from React.
 */
export function pickStandIn(
  dealId: string,
  category: string,
  own: readonly string[],
  photos: readonly PoolPhoto[],
  brokenUrls: ReadonlySet<string>,
): string | null {
  const usable = [...new Set(photos.filter((p) => !brokenUrls.has(p.url) && !own.includes(p.url)).map((p) => p.url))];

  if (usable.length === 0) return null;

  const sameCategory = new Set(photos.filter((p) => p.category === category).map((p) => p.url));
  const preferred = usable.filter((url) => sameCategory.has(url));
  const from = (preferred.length > 0 ? preferred : usable).sort();

  return from[hash(dealId) % from.length] ?? null;
}

/**
 * The stand-in photo for a deal, when `needed`. Re-renders when the pool arrives or
 * a photo is found broken.
 */
export function useStandInPhoto(
  dealId: string,
  category: string,
  own: readonly (string | null)[],
  needed: boolean,
): string | null {
  useSyncExternalStore(subscribe, () => version);

  useEffect(() => {
    if (needed) ensurePool();
  }, [needed]);

  if (!needed) return null;

  const ownUrls = own.filter((url): url is string => url !== null);
  return pickStandIn(dealId, category, ownUrls, pool, broken);
}
